import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PatientRow from './PatientRow';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';

/**
 * PatientTable - Patients table with search, status filter and pagination.
 * A página, a busca e o status são aplicados no servidor (usePatientsPage):
 * a tabela só mostra as linhas recebidas e avisa as mudanças.
 * @param {Object} props
 * @param {Array} props.patients - Rows of the current page
 * @param {number} props.totalItems - Total matching the search and status
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.filter - Search text
 * @param {string} props.statusFilter - '' | '1' | '0'
 * @param {number} props.currentPage - Zero-based page
 * @param {number} props.rowsPerPage - Rows per page
 * @param {Function} props.onFilterChange - (text) => void
 * @param {Function} props.onStatusFilterChange - (status) => void
 * @param {Function} props.onPageChange - (zeroBasedPage) => void
 * @param {Function} props.onRowsPerPageChange - (rows) => void
 * @param {Function} props.onViewPatient - Callback to view patient profile
 * @param {Function} props.onSchedulePatient - Callback to schedule appointment
 * @param {Function} props.onEditPatient - Callback to edit patient
 */
function PatientTable({
  patients = [], totalItems = 0, isLoading = false,
  filter = '', statusFilter = '', currentPage = 0, rowsPerPage = 10,
  onFilterChange, onStatusFilterChange, onPageChange, onRowsPerPageChange,
  onViewPatient, onSchedulePatient, onEditPatient,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 760px)');
  // "Só os meus pacientes": a API já filtra, a lista vazia explica o porquê
  const { hasOwnScheduleScope } = useRoleAccess();

  const paginatedPatients = patients;

  const handleFilterChange = (e) => onFilterChange?.(e.target.value);

  const handleStatusFilterChange = (e) => onStatusFilterChange?.(e.target.value);

  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startItem = totalItems === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * rowsPerPage, totalItems);

  return (
    <Paper
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          borderBottom: '1px solid #E8ECF0',
          bgcolor: '#fff',
        }}
      >
        {/* Campo de busca */}
        <TextField
          size="small"
          placeholder="Buscar por nome, CPF, telefone ou e-mail"
          value={filter}
          onChange={handleFilterChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            maxWidth: { sm: 480 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#fff',
              height: '40px',
              '& fieldset': { borderColor: '#E0E0E0' },
              '&:hover fieldset': { borderColor: '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
            '& input': { fontSize: '0.875rem' },
          }}
        />

        {/* Select de status */}
        <Select
          size="small"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          displayEmpty
          sx={{
            height: '40px',
            borderRadius: '8px',
            fontSize: '0.875rem',
            minWidth: 160,
            backgroundColor: '#fff',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
          }}
        >
          <MenuItem value="">Todos os status</MenuItem>
          <MenuItem value="1">Ativo</MenuItem>
          <MenuItem value="0">Inativo</MenuItem>
        </Select>
      </Box>

      {/* Table */}
      <TableContainer sx={{ boxShadow: 'none', borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FC' }}>
              {/* Sempre visível */}
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                Paciente
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem' }}>
                Contato
              </TableCell>
              {/* Ocultar em mobile (<760px) */}
              {!isMobile && (
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                  Nascimento
                </TableCell>
              )}
              {!isMobile && (
                <TableCell align="left" sx={{ width: 100, fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary' }}>
                  Status
                </TableCell>
              )}
              {/* Sempre visível */}
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem' }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 3 : 5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : paginatedPatients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 3 : 5} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {filter || statusFilter
                      ? 'Nenhum paciente encontrado com os filtros aplicados.'
                      : hasOwnScheduleScope()
                        ? 'Você ainda não atendeu nem cadastrou ninguém. Aqui aparecem as pessoas que você atende ou cadastra.'
                        : 'Ninguém cadastrado ainda.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPatients.map((patient) => (
                <PatientRow
                  key={patient.id}
                  patient={patient}
                  isMobile={isMobile}
                  onView={onViewPatient}
                  onSchedule={onSchedulePatient}
                  onEdit={onEditPatient}
                  highlight={filter}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rodapé de paginação */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 0,
        px: 3,
        py: 2,
        borderTop: '1px solid #F0F0F0',
        flexWrap: 'wrap',
        gap: 2,
      }}>
        {/* Texto à esquerda */}
        <Typography variant="body2" color="text.secondary">
          Mostrando {startItem} a {endItem} de {totalItems} pacientes
        </Typography>

        {/* Paginação no centro */}
        <Pagination
          count={totalPages}
          page={currentPage + 1}
          onChange={(_, page) => onPageChange?.(page - 1)}
          shape="rounded"
          color="primary"
          size="small"
          siblingCount={2}
          boundaryCount={1}
        />

        {/* Select de itens por página à direita */}
        <Select
          size="small"
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange?.(Number(e.target.value))}
          renderValue={(val) => `${val} por página`}
          sx={{
            fontSize: '0.8125rem',
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
          }}
        >
          <MenuItem value={10}>10 por página</MenuItem>
          <MenuItem value={25}>25 por página</MenuItem>
          <MenuItem value={50}>50 por página</MenuItem>
        </Select>
      </Box>
    </Paper>
  );
}

export default PatientTable;
